<section class="gen-management-page">
  <div class="page-shell">
    <header class="page-header app-card">
      <div>
        <h1>{{ title() }}</h1>
        <span>{{ description() }}</span>
      </div>
    </header>

    <div class="stats-grid">
      @for (card of stats(); track card.label) {
        <article class="stat-card app-card" [class]="'tone-' + card.tone">
          <div class="stat-icon">{{ card.icon }}</div>
          <div class="stat-copy">
            <span>{{ card.label }}</span>
            <strong>
              {{ card.value }}
              @if (card.suffix) {
                <small>{{ card.suffix }}</small>
              }
            </strong>
          </div>
        </article>
      }
    </div>

    <section class="table-card app-card">
      <div class="filter-grid">
        <label class="filter-field">
          <span>Search Unit</span>
          <input
            class="app-form-input"
            type="search"
            placeholder="Enter unit name / code"
            [value]="searchTerm()"
            (input)="updateSearch($any($event.target).value)"
          >
        </label>

        <label class="filter-field">
          <span>QCA</span>
          <select class="app-form-select" [value]="selectedQca()" (change)="updateQca($any($event.target).value)">
            @for (option of filters().qca; track option) {
              <option [value]="option === 'All' ? option : option.split(' (')[0]">{{ option }}</option>
            }
          </select>
        </label>

        <label class="filter-field">
          <span>PSS</span>
          <select class="app-form-select" [value]="selectedPss()" (change)="updatePss($any($event.target).value)">
            @for (option of filters().pss; track option) {
              <option [value]="option">{{ option }}</option>
            }
          </select>
        </label>

        <label class="filter-field">
          <span>Technology</span>
          <select class="app-form-select" [value]="selectedTechnology()" (change)="updateTechnology($any($event.target).value)">
            @for (option of filters().technology; track option) {
              <option [value]="option">{{ option }}</option>
            }
          </select>
        </label>

        <label class="filter-field">
          <span>Status</span>
          <select class="app-form-select" [value]="selectedStatus()" (change)="updateStatus($any($event.target).value)">
            @for (option of filters().status; track option) {
              <option [value]="option">{{ option }}</option>
            }
          </select>
        </label>

        <div class="capacity-field">
          <span>Capacity Range (MW)</span>
          <div class="capacity-inputs">
            <input
              class="app-form-input"
              type="number"
              min="0"
              placeholder="Min"
              [value]="minCapacity()"
              (input)="updateMinCapacity($any($event.target).value)"
            >
            <span class="capacity-divider">-</span>
            <input
              class="app-form-input"
              type="number"
              min="0"
              placeholder="Max"
              [value]="maxCapacity()"
              (input)="updateMaxCapacity($any($event.target).value)"
            >
          </div>
        </div>

        <div class="filter-actions">
          <button type="button" class="app-button app-button--primary">Search</button>
          <button type="button" class="app-button app-button--secondary" (click)="resetFilters()">Reset</button>
        </div>
      </div>

      <div class="table-wrap">
        <table class="gen-table table-sm menu-table">
          <thead>
            <tr>
              <th>Company Code</th>
              <th>Company Name</th>
              <th>QCA Name</th>
              <th>PSS Name</th>
              <th>Technology</th>
              <th>Rated Capacity (MW)</th>
              <th>Available Capacity (MW)</th>
              <th>Commission Date</th>
              <th>Status</th>
              <th>Operational Since</th>
              <th>Remarks</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            @for (row of paginatedRows(); track row.code) {
              <tr>
                <td><a href="" (click)="$event.preventDefault()">{{ row.code }}</a></td>
                <td>{{ row.name }}</td>
                <td><button type="button" class="table-link name-link" (click)="goToQcaDashboard(row.qcaCode)">{{ row.qcaName }}</button></td>
                <td><button type="button" class="table-link name-link" (click)="goToPssDashboard(row.pssCode)">{{ row.pssName }}</button></td>
                <td>{{ row.technology }}</td>
                <td>{{ formatCapacity(row.ratedCapacityMw) }}</td>
                <td>{{ formatCapacity(row.availableCapacityMw) }}</td>
                <td>{{ row.commissionDate }}</td>
                <td>
                  <span class="status-pill" [class]="'status-' + row.status.toLowerCase().replace(' ', '-')">
                    {{ row.status }}
                  </span>
                </td>
                <td>{{ row.operationalSince }}</td>
                <td>{{ row.remarks }}</td>
                <td>
                  <button type="button" class="manage-button" (click)="openManageGeneratorsModal(row.code)">
                    Manage Generators
                  </button>
                </td>
              </tr>
            } @empty {
              <tr>
                <td colspan="12" class="empty-state">No generating company records found for the selected filters.</td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <footer class="table-footer">
        <span>{{ showingText() }}</span>

        <div class="pagination">
          <button type="button" class="page-button" [disabled]="currentPage() === 1" (click)="goToPage(currentPage() - 1)">‹</button>
          @for (page of pageNumbers(); track page) {
            <button
              type="button"
              class="page-button"
              [class.active]="page === currentPage()"
              (click)="goToPage(page)"
            >
              {{ page }}
            </button>
          }
          <button type="button" class="page-button" [disabled]="currentPage() === totalPages()" (click)="goToPage(currentPage() + 1)">›</button>
        </div>
      </footer>
    </section>

    @if (isManageGeneratorsModalOpen() && selectedCompany()) {
      <div class="modal-backdrop" role="presentation">
        <div class="manage-generators-modal app-card" role="dialog" aria-modal="true" aria-labelledby="manage-generators-title">
          <div class="modal-header">
            <div>
              <h2 id="manage-generators-title">Manage Generators for {{ selectedCompany()?.code }} - {{ selectedCompany()?.name }}</h2>
            </div>
            <button type="button" class="modal-close" (click)="closeManageGeneratorsModal()" aria-label="Close manage generators modal">×</button>
          </div>

          <div class="modal-tabs">
            <button
              type="button"
              class="tab-button"
              [class.active]="activeGeneratorTab() === 'available'"
              (click)="switchGeneratorTab('available')"
            >
              Available Generators
            </button>
            <button
              type="button"
              class="tab-button"
              [class.active]="activeGeneratorTab() === 'linked'"
              (click)="switchGeneratorTab('linked')"
            >
              Linked Generators ({{ linkedGenerators().length }})
            </button>
          </div>

          <div class="modal-body">
            @if (activeGeneratorTab() === 'available') {
              <div class="modal-toolbar">
                <label class="modal-search">
                  <input
                    class="app-form-input"
                    type="search"
                    placeholder="Search available generators..."
                    [value]="availableGeneratorSearch()"
                    (input)="updateAvailableGeneratorSearch($any($event.target).value)"
                  >
                </label>

                <button
                  type="button"
                  class="add-selected-button"
                  [disabled]="selectedAvailableGeneratorCodes().length === 0"
                  (click)="addSelectedGenerators()"
                >
                  Add Selected
                </button>
              </div>

              <div class="modal-table-wrap">
                <table class="modal-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>Unit Code</th>
                      <th>Unit Name</th>
                      <th>Technology</th>
                      <th>Rated Capacity (MW)</th>
                      <th>Status</th>
                      <th>Commission Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (item of filteredAvailableGenerators(); track item.code) {
                      <tr>
                        <td>
                          <input
                            type="checkbox"
                            [checked]="selectedAvailableGeneratorCodes().includes(item.code)"
                            (change)="toggleAvailableGeneratorSelection(item.code, $any($event.target).checked)"
                          >
                        </td>
                        <td>{{ item.code }}</td>
                        <td>{{ item.name }}</td>
                        <td>{{ item.technology }}</td>
                        <td>{{ formatCapacity(item.ratedCapacityMw) }}</td>
                        <td>
                          <span class="status-pill" [class]="'status-' + item.status.toLowerCase().replace(' ', '-')">
                            {{ item.status }}
                          </span>
                        </td>
                        <td>{{ item.commissionDate }}</td>
                      </tr>
                    } @empty {
                      <tr>
                        <td colspan="7" class="empty-state">No available generators found for this company.</td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            } @else {
              <div class="modal-toolbar">
                <label class="modal-search">
                  <input
                    class="app-form-input"
                    type="search"
                    placeholder="Search linked generators..."
                    [value]="linkedGeneratorSearch()"
                    (input)="updateLinkedGeneratorSearch($any($event.target).value)"
                  >
                </label>

                <button
                  type="button"
                  class="remove-selected-button"
                  [disabled]="selectedLinkedGeneratorCodes().length === 0"
                  (click)="removeSelectedGenerators()"
                >
                  Remove Selected
                </button>
              </div>

              <div class="modal-table-wrap">
                <table class="modal-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>Unit Code</th>
                      <th>Unit Name</th>
                      <th>Technology</th>
                      <th>Rated Capacity (MW)</th>
                      <th>Status</th>
                      <th>Commission Date</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (item of filteredLinkedGenerators(); track item.code) {
                      <tr>
                        <td>
                          <input
                            type="checkbox"
                            [checked]="selectedLinkedGeneratorCodes().includes(item.code)"
                            (change)="toggleLinkedGeneratorSelection(item.code, $any($event.target).checked)"
                          >
                        </td>
                        <td>{{ item.code }}</td>
                        <td>{{ item.name }}</td>
                        <td>{{ item.technology }}</td>
                        <td>{{ formatCapacity(item.ratedCapacityMw) }}</td>
                        <td>
                          <span class="status-pill" [class]="'status-' + item.status.toLowerCase().replace(' ', '-')">
                            {{ item.status }}
                          </span>
                        </td>
                        <td>{{ item.commissionDate }}</td>
                        <td>
                          <button type="button" class="remove-icon-button" (click)="removeLinkedGenerator(item.code)" aria-label="Remove linked generator">🗑</button>
                        </td>
                      </tr>
                    } @empty {
                      <tr>
                        <td colspan="8" class="empty-state">No linked generators found for this company.</td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            }
          </div>

          <div class="modal-footer">
            <button type="button" class="app-button app-button--primary" (click)="closeManageGeneratorsModal()">Close</button>
          </div>
        </div>
      </div>
    }
  </div>
</section>
